import 'react-perfect-scrollbar/dist/css/styles.css';

import type React from 'react';
import { Component } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { ConfigContext } from '../context/ConfigContext';
import {
  type ChatBubbleInfo,
  type Message,
  type MessagePayload,
  Sender,
} from '../types/ChatWindow';
import type { MessageScheme } from '../types/configs';
import Avatar from './Avatar';
import TimeStamp from './TimeStamp';
import TypingIndicator from './TypingIndicator';
import ButtonedImageMessage from './message/ButtonedImageMessage';
import ButtonedTextMessage from './message/ButtonedTextMessage';
import { DropdownMessage } from './message/DropdownMessage';
import ImageMessage from './message/ImageMessage';
import { RatingMessage } from './message/RatingMessage';
import SystemMessage from './message/SystemMessage';
import ImageCarousel from './message/carousel/ImageCarousel';
import TextMessage from './message/text/TextMessage';

class Messages extends Component<
  {
    messages: Array<Message>;
    submitHandler: (...args: any[]) => void;
    typingState: 'typing_on' | 'typing_off';
  },
  { isTyping: boolean }
> {
  static contextType = ConfigContext;
  context!: React.ContextType<typeof ConfigContext>;
  scrollContainer: any = null;

  constructor(props: {
    messages: Array<Message>;
    submitHandler: (...args: any[]) => void;
    typingState: 'typing_on' | 'typing_off';
  }) {
    super(props);
    const { typingState } = this.props;
    this.state = {
      isTyping: typingState === 'typing_on',
    };
  }

  componentDidMount() {
    const { messages } = this.props;
    // If there are messages stored then scroll to the bottom
    if (messages.length > 0 && this.scrollContainer) {
      this.scrollToBottom(this.scrollContainer);
    }
  }

  componentDidUpdate(prevProps: {
    messages: Array<Message>;
    typingState: 'typing_on' | 'typing_off';
  }) {
    const { messages, typingState } = this.props;
    // Comparing props to update
    if (messages !== prevProps.messages) {
      // Scroll to bottom
      this.scrollToBottom(this.scrollContainer);
      if (typingState === prevProps.typingState) {
        this.setTyping(prevProps.typingState);
      }
    }
    if (typingState !== prevProps.typingState) {
      this.setTyping(typingState);
    }
    if (typingState) {
      this.scrollToBottom(this.scrollContainer);
    }
  }

  setTyping(action: 'typing_on' | 'typing_off') {
    const isTyping = action === 'typing_on';
    this.setState({
      isTyping,
    });
  }

  // Take a PerfectScrollBar reference and scrolls to the (potential) bottom of it, since we cannot know the height we have to make a guess
  scrollToBottom = (ref: any) => {
    ref.scrollTop = this.scrollContainer.scrollHeight;
  };

  useStyle = (message: Message, style: MessageScheme) => {
    const styles = {};
    const messageType = message?.payload?.type;

    // * For both text and textWithButtons set the styles for urls
    if (['text', 'textWithButtons'].includes(messageType)) {
      Object.assign(styles, {
        '--url-color': style.urlColor,
        '--url-hover-color': style.urlHoverColor,
        '--url-click-color': style.urlClickColor,
        '--url-text-decoration': style.urlTextDecoration,
      });
    }

    // * Set colors only for text blocks
    if (messageType === 'text') {
      Object.assign(styles, {
        color: style.messageBubbleTextColor,
        backgroundColor: style.messageBubbleColor,
      });
    }

    return styles;
  };

  /**
   * returns the style of the message in case of text and textwithbuttons
   * @param message user conversations messages
   * @param messageScheme user defined config scheme
   */
  prepareStyle = (
    message: Message,
    messageScheme: MessageScheme,
  ): MessageScheme => {
    const {
      personalMessageBubbleColor,
      personalMessageBubbleTextColor,
      messageBubbleColor,
      messageBubbleTextColor,
    } = messageScheme;
    const bubbleColor =
      message.direction === 'out'
        ? messageBubbleColor
        : personalMessageBubbleColor;
    const bubbleTextColor =
      message.direction === 'out'
        ? messageBubbleTextColor
        : personalMessageBubbleTextColor;
    return {
      ...messageScheme,
      messageBubbleColor: bubbleColor,
      messageBubbleTextColor: bubbleTextColor,
    };
  };

  /**
   * Chuck messages, turn [] into [[]], with a new inner array for every direction switch
   * @param messages user conversations messages
   */
  chunkMessageStructure = (messages: Message[]) => {
    const messageSets: Message[][] = [];
    let messagesHelper: Message[] = [];
    let currentDirection = messages[0].direction;
    messages.forEach((currentMessage) => {
      if (currentDirection !== currentMessage.direction) {
        messageSets.push(messagesHelper);
        messagesHelper = [];
      }
      messagesHelper.push(currentMessage);
      currentDirection = currentMessage.direction;
    });

    messageSets.push(messagesHelper);
    return messageSets;
  };

  getChatBubbleInfo = (
    message: Message,
    defaultAvatarImg: string,
  ): ChatBubbleInfo => {
    if (
      'options' in message.payload &&
      message?.payload?.options &&
      'agentData' in message.payload.options &&
      message.payload.options.agentData
    ) {
      const { firstName, profilePic } = message.payload.options.agentData;

      return {
        agentName: (firstName ?? '').trim(),
        avatarImg: profilePic ?? defaultAvatarImg,
      };
    }

    return { agentName: '', avatarImg: defaultAvatarImg };
  };

  getMessageStructure = (
    messages: Message[],
    messageScheme: MessageScheme & { messageIcon: string },
    notificationAIGeneratedAnswer: string,
  ) => {
    const { messageIcon } = messageScheme;
    if (messages && messages.length > 0) {
      const setOfMessages = this.chunkMessageStructure(messages);

      return setOfMessages.map((messageSet, indexSet) => {
        return (
          <div className='message-container' key={`${indexSet}-set`}>
            {messageSet.map((message, index) => {
              const style = this.prepareStyle(message, messageScheme);
              const isLastMessage =
                setOfMessages.length === indexSet + 1 &&
                messageSet.length === index + 1;

              const { agentName, avatarImg } = this.getChatBubbleInfo(
                message,
                messageIcon,
              );

              const isAIMessage =
                'options' in message.payload &&
                message.payload?.options &&
                'sentBy' in message.payload.options &&
                message.payload.options?.sentBy === Sender.AI;

              return (
                <>
                  {message.direction === 'system' ? (
                    <div key={index} className='system-message-direction'>
                      {this.renderSwitch(message.payload, style, isLastMessage)}
                    </div>
                  ) : (
                    <>
                      {agentName && (
                        <div className='agent-name'>{agentName}</div>
                      )}
                      <div
                        key={index}
                        className={`message-row ${message.direction === 'in' ? 'direction-in' : 'direction-out'}`}>
                        <div
                          className={`message-content ${message?.payload?.type ?? ''} ${message.direction === 'in' ? 'direction-in' : 'direction-out'}`}
                          style={this.useStyle(message, style)}>
                          {message.direction === 'out' ? (
                            <Avatar avatarImg={avatarImg} />
                          ) : null}
                          {this.renderSwitch(
                            message.payload,
                            style,
                            isLastMessage,
                          )}
                        </div>
                        <div
                          className={`message-footer ${message.direction === 'in' ? 'direction-in' : 'direction-out'}`}>
                          {messages.length === index + 1 ? <TimeStamp /> : null}
                          {isAIMessage ? (
                            <span className='notification-ai-generated-answer'>
                              {notificationAIGeneratedAnswer}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </>
                  )}
                </>
              );
            })}
          </div>
        );
      });
    }
    return null;
  };

  renderSwitch(
    message: MessagePayload,
    scheme: MessageScheme,
    isLastMessage: boolean,
  ) {
    const { submitHandler } = this.props;

    switch (message.type) {
      case 'text':
        return <TextMessage data={message.data} />;
      case 'image':
        return <ImageMessage data={message.data} />;
      case 'textWithButtons':
        return (
          <ButtonedTextMessage
            data={message.data}
            scheme={scheme}
            submitHandler={submitHandler}
          />
        );
      case 'imageWithButtons': // Deprecated, it is not used anymore
        return (
          <ButtonedImageMessage
            data={message.data}
            scheme={scheme}
            submitHandler={submitHandler}
          />
        );
      case 'carousel':
        return (
          <ImageCarousel
            data={message.data}
            options={message?.options}
            scheme={scheme}
            submitHandler={submitHandler}
          />
        );
      case 'feedback':
        return (
          <RatingMessage
            scheme={scheme}
            submitHandler={submitHandler}
            data={message.data}
            isLastMessage={isLastMessage}
          />
        );
      case 'dropdown':
        return (
          <DropdownMessage
            scheme={scheme}
            submitHandler={submitHandler}
            data={message.data}
            isLastMessage={isLastMessage}
          />
        );
      case 'system': {
        const {
          scheme: {
            header: { windowColor, headerColor },
          },
        } = this.context;

        return (
          <SystemMessage
            data={message.data}
            textColor={headerColor}
            windowColor={windowColor}
          />
        );
      }
      default:
        return null;
    }
  }

  render() {
    const { messages } = this.props;
    const { isTyping } = this.state;
    const {
      scheme: {
        header: { windowColor },
        message: { messageBubbleColor, messageBubbleTextColor },
        message: messageScheme,
      },
      messageIcon,
      welcomeMessage,
      showWelcomeAsFirstMessage,
      notificationAIGeneratedAnswer,
    } = this.context;

    return (
      <div
        className='messages-container'
        id='messages'
        style={{ backgroundColor: windowColor }}>
        <PerfectScrollbar
          containerRef={(container) => {
            this.scrollContainer = container;
          }}
          options={{ suppressScrollX: true }}>
          {showWelcomeAsFirstMessage &&
            welcomeMessage &&
            welcomeMessage.length > 0 && (
              <div className='message-container'>
                <div className='message-row direction-out'>
                  <div
                    className='message-content welcome'
                    style={{
                      backgroundColor: messageBubbleColor,
                      color: messageBubbleTextColor,
                    }}>
                    {welcomeMessage}
                  </div>
                  <div className='message-footer direction-out'>
                    {messages.length === 0 ? <TimeStamp /> : null}
                  </div>
                </div>
              </div>
            )}
          {messages
            ? this.getMessageStructure(
                messages,
                {
                  ...messageScheme,
                  messageIcon,
                },
                notificationAIGeneratedAnswer,
              )
            : null}
          <TypingIndicator show={isTyping} />
        </PerfectScrollbar>
      </div>
    );
  }
}

export default Messages;
